import React, { useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import PageBanner from "../components/PageBanner";
import CategoryImageGallery from "../components/CategoryImageGallery";
import Breadcrumbs from "../components/Breadcrumbs";
import JsonLd from "../components/JsonLd";
import { getCategoryAssets } from "../utils/categoryAssets";
import { pairSubeventsWithGallery } from "../utils/pairSubeventsWithGallery";
import { buildBreadcrumbListJsonLd } from "../utils/breadcrumbSchema";
import { buildProductionServiceGraph } from "../schema/productionServiceGraph";
import {
  productionIntro,
  productionServices,
} from "../constants/productionData";
import "../components/EventCategoryLayout.css";
import "../components/homeGraphic.css";
import "./Production.css";

const Production = () => {
  const location = useLocation();
  const introParagraphs = productionIntro.description.trim().split(/\n\n+/);

  const { bannerSrc, gallery } = useMemo(
    () => getCategoryAssets("production"),
    [],
  );

  const servicesWithImages = useMemo(() => {
    const paired = pairSubeventsWithGallery(
      productionServices.map((s) => ({
        name: s.title,
        description: s.description,
      })),
      gallery,
    );
    return productionServices.map((service, index) => ({
      ...service,
      image: paired[index]?.image ?? null,
    }));
  }, [gallery]);

  useEffect(() => {
    const hash = (location.hash || window.location.hash).slice(1);
    if (hash) {
      const el = document.getElementById(hash);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, [location.pathname, location.hash]);

  const breadcrumbLd = buildBreadcrumbListJsonLd([
    { name: "Home", path: "/" },
    { name: "Production", path: "/production" },
  ]);
  const productionGraph = buildProductionServiceGraph();

  return (
    <div className="production-page">
      <JsonLd data={breadcrumbLd} />
      <JsonLd data={productionGraph} />
      <Breadcrumbs
        items={[{ label: "Home", to: "/" }, { label: "Production" }]}
      />
      <PageBanner
        title={productionIntro.title}
        subtitle={productionIntro.tagline}
        image={bannerSrc}
        imageAlt={productionIntro.bannerImageAlt || ""}
        imageWidth={1920}
        imageHeight={1080}
      />

      <section className="production-section event-category-section">
        <div className="production-container event-category-container">
          <div className="production-intro event-category-intro">
            <span className="production-badge">Our Services</span>
            <h2 className="production-intro-title">
              Complete Event Production
            </h2>
            <div className="title-underline"></div>
            {introParagraphs.map((para, index) => (
              <p key={index} className="production-intro-para">
                {para}
              </p>
            ))}
          </div>

          <section
            className="event-category-services production-services-zigzag"
            aria-labelledby="production-services-heading"
          >
            <div className="event-category-header event-category-services-header production-services-menu-header">
              <h2
                id="production-services-heading"
                className="event-category-heading"
              >
                Production & Services Menu
              </h2>
              <div className="title-underline" aria-hidden="true" />
              <p className="event-category-services-tagline production-menu-tagline">
                Explore our full range of production services — each designed to
                make your event seamless and memorable.
              </p>
            </div>

            <div className="event-category-services-grid">
              {servicesWithImages.map((service, index) => (
                <article
                  key={service.id}
                  id={service.id}
                  className={`event-category-service-row${
                    index % 2 === 1
                      ? " event-category-service-row--reverse"
                      : ""
                  }`}
                >
                  <div className="event-category-service__graphic">
                    {service.image ? (
                      <div className="home-graphic-wrap home-graphic-wrap--expertise-row">
                        <img
                          src={service.image.src}
                          alt={service.image.alt || service.title}
                          className="home-graphic-img"
                          width={800}
                          height={500}
                          loading={index > 0 ? "lazy" : "eager"}
                          decoding="async"
                        />
                      </div>
                    ) : (
                      <div
                        className="event-category-service__placeholder"
                        aria-hidden="true"
                      />
                    )}
                  </div>
                  <div className="event-category-service__body">
                    <h3 className="event-category-service__title">
                      {service.title}
                    </h3>
                    <p className="event-category-service__text">
                      {service.description}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </section>

          {/* <CategoryImageGallery
            items={gallery}
            title="Production gallery"
            className="production-image-gallery"
          />

          <nav className="production-related" aria-label="Related services">
            <h2 className="production-related-title">Related services</h2>
            <div className="title-underline"></div>
            <p className="production-related-text">
              Planning an end-to-end programme? Explore{" "}
              <Link to="/wedding-events">wedding events</Link>,{" "}
              <Link to="/corporate-events">corporate events</Link>, and{" "}
              <Link to="/concert-events">concerts</Link>. Local SEO guides:{" "}
              <Link to="/event-management-ahmedabad">event management Ahmedabad</Link>,{" "}
              <Link to="/wedding-planner-ahmedabad">wedding planner Ahmedabad</Link>.
            </p>
          </nav> */}

          <div className="production-cta">
            <h2 className="production-cta-title">Ready to Plan Your Event?</h2>
            <p className="production-cta-text">
              Tell us your vision — we'll handle the production, from stage and
              décor to technical support and logistics.
            </p>
            <Link to="/contact" className="production-cta-btn">
              Get in Touch
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Production;
